import { Link } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

import i18n from "@/i18n";
import Button from "@/components/button";
import { Divider } from "@/components/divider";
import { LeftArrow, LinkArrowLeft } from "@/components/icons";
import { ShiftCardItem } from "@/components/shift-card";
import { useOpenShift } from "@/components/shift-detail/use-shift-modal";
import { shiftHasStarted } from "@/utils/shift-card";
import type { ShiftCard } from "@/utils/shift-card";
import { localeFor } from "@/utils/formatters";
import { useRole } from "@/utils/hooks/use-role";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export type SidebarState =
  | { type: "list"; items: Array<ShiftCard>; showPast: boolean }
  | { type: "detail"; shift: ShiftCard }
  | { type: "day"; date: string; shifts: Array<ShiftCard> }
  | { type: "past"; items: Array<ShiftCard> };

export function ScheduleSidebar({
  state,
  onSelectShift,
  onBack,
}: {
  state: SidebarState;
  onSelectShift: (shift: ShiftCard) => void;
  onBack: () => void;
  onSeePast: () => void;
}) {
  const { t } = useTranslation("dashboard");
  const role = useRole();
  const openShift = useOpenShift();
  if (state.type === "detail") {
    const s = state.shift;
    // A shift "has occurred" once it has started. Editing is only for future shifts;
    // reviewing is only for shifts that have already happened.
    const occurred = shiftHasStarted(s.startDateTime);
    return (
      <aside className="relative h-full p-5 bg-white rounded-[15px] flex flex-col min-w-[400px] max-w-[400px] min-h-[740px] gap-3 max-h-[740px]">
        <Button variant="outline" className="w-[120px]" onClick={onBack}>
          <span className="flex items-center gap-[2px] w-full">
            <LeftArrow /> {t("schedule.back")}
          </span>
        </Button>
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-2 items-center">
            <Avatar>
              <AvatarImage
                src={
                  role === "worker"
                    ? state.shift.businessAvatar
                    : state.shift.workerAvatar
                }
              />
              <AvatarFallback>
                {role === "worker"
                  ? state.shift.businessName
                  : state.shift.workerName}
              </AvatarFallback>
            </Avatar>
            {role === "worker" && s.businessName && (
              <Link
                to={"/app/profile/employer/$employerId"}
                params={{ employerId: state.shift.businessUserId! }}
                className="no-underline!"
              >
                <div className="text-lg font-semibold text-garnet/90 mt-2">
                  {s.businessName}
                </div>
              </Link>
            )}
            {role === "employer" &&
              (() => {
                return null;
              })()}
            {role === "employer" && (
              <Link
                to={"/app/profile/worker/$workerId"}
                params={{ workerId: state.shift.workerId ?? "" }}
                className="no-underline!"
              >
                <div className="text-lg font-semibold text-garnet/90 mt-2">
                  {s.workerName}
                </div>
              </Link>
            )}
            <div className="text-sm text-garnet/70">
              {s.dateLabel
                ? new Date(s.dateLabel).toLocaleDateString(localeFor(i18n.language || "en"), {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                : ""}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 body-small">
          <div className="bg-fog w-full p-2">
            <span>{t("schedule.shift")} </span>
            <span className="font-bold">
              {s.dateLabel
                ? new Date(s.dateLabel).toLocaleDateString(localeFor(i18n.language || "en"), {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                : ""}
            </span>
          </div>
          <div className="bg-fog w-full p-2">
            <span className="font-bold">{s.timeLabel}</span>
          </div>
        </div>
        <div className="bg-fog w-full p-2">
          <span>{t("schedule.location")} </span>
          <span className="font-bold">{s.venue}</span>
        </div>
        <div className="bg-fog w-full p-2">
          <span>{t("schedule.wage")} </span>
          <span className="font-bold">
            {t("schedule.wageValue", {
              rate: s.ratePerHour,
              total: s.total,
            })}
          </span>
        </div>
        <div className="bg-fog w-full p-2">{s.description ?? ""}</div>

        <Divider />
        <div className="w-full flex flex-col mt-12 border-t-[1px] border-[#545A6340] border-b-[1px]">
          <Link
            to="/app/$role/messages"
            params={{ role }}
            search={{
              to: role === "worker" ? s.businessUserId : s.workerId,
              tab: "messages",
              businessId: undefined,
              selectedOfferId: undefined,
            }}
            className="flex flex-row items-center w-full justify-between h-10 body-small font-semibold hover:bg-fog rounded px-2 no-underline!"
            aria-label={t("schedule.messageAria", { name: s.businessName })}
          >
            <div className="flex flex-row items-center justify-center gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M7.5 16.25C7.5 15.5586 6.94141 15 6.25 15H2.5C1.80859 15 1.25 14.4414 1.25 13.75V2.5C1.25 1.80859 1.80859 1.25 2.5 1.25H17.5C18.1914 1.25 18.75 1.80859 18.75 2.5V13.75C18.75 14.4414 18.1914 15 17.5 15H12.082C11.8125 15 11.5469 15.0859 11.332 15.25L7.5 18.125V16.25ZM2.5 0C1.12109 0 0 1.12109 0 2.5V13.75C0 15.1289 1.12109 16.25 2.5 16.25H5H6.25V17.5V19.375C6.25 19.6133 6.38281 19.8281 6.59375 19.9336C6.80469 20.0391 7.05859 20.0156 7.25 19.875L12.082 16.25H17.5C18.8789 16.25 20 15.1289 20 13.75V2.5C20 1.12109 18.8789 0 17.5 0H2.5ZM5.625 5.625C5.28125 5.625 5 5.90625 5 6.25C5 6.59375 5.28125 6.875 5.625 6.875H14.375C14.7188 6.875 15 6.59375 15 6.25C15 5.90625 14.7188 5.625 14.375 5.625H5.625ZM5.625 9.375C5.28125 9.375 5 9.65625 5 10C5 10.3438 5.28125 10.625 5.625 10.625H10.625C10.9688 10.625 11.25 10.3438 11.25 10C11.25 9.65625 10.9688 9.375 10.625 9.375H5.625Z"
                  fill="black"
                />
              </svg>
              {role === "worker"
                ? t("schedule.messageBusiness")
                : t("schedule.messageWorker")}
            </div>
            <LinkArrowLeft />
          </Link>
          <Divider />
          {role === "employer" && !occurred && (
            <>
              <Link
                to="/app/$role/shifts/$shiftId/edit"
                params={{ role, shiftId: String(s.id) }}
                className="flex flex-row items-center w-full justify-between h-10 body-small font-semibold hover:bg-fog rounded px-2 no-underline!"
                aria-label={t("schedule.editShift")}
              >
                <div className="flex flex-row items-center justify-center gap-3">
                  <Pencil className="h-5 w-5" strokeWidth={1.75} />
                  {t("schedule.editShift")}
                </div>
                <LinkArrowLeft />
              </Link>
              <Divider />
            </>
          )}
          {occurred && (
            <Link
              to={
                role === "worker"
                  ? "/app/profile/employer/$employerId/review"
                  : "/app/profile/worker/$workerId/review"
              }
              params={{
                workerId: state.shift.workerId ?? "",
                employerId: state.shift.businessUserId,
              }}
              className="no-underline!"
            >
              <div className="flex flex-row items-center w-full justify-between h-10 body-small  font-semibold">
                <div className="flex flex-row items-center justify-center gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M13.7513 0.625V3.125H16.2513C16.5951 3.125 16.8763 3.40625 16.8763 3.75C16.8763 4.09375 16.5951 4.375 16.2513 4.375H13.7513V6.875C13.7513 7.21875 13.4701 7.5 13.1263 7.5C12.7826 7.5 12.5013 7.21875 12.5013 6.875V4.375H10.0013C9.65758 4.375 9.37633 4.09375 9.37633 3.75C9.37633 3.40625 9.65758 3.125 10.0013 3.125H12.5013V0.625C12.5013 0.28125 12.7826 0 13.1263 0C13.4701 0 13.7513 0.28125 13.7513 0.625ZM5.97008 10.3711C5.78649 10.7422 5.43493 10.9961 5.02868 11.0547L1.61071 11.5547L4.08727 13.9727C4.38024 14.2578 4.51696 14.6719 4.44665 15.0781L3.86071 18.4883L6.9154 16.8789C7.27868 16.6875 7.71618 16.6875 8.07946 16.8789L11.1341 18.4883L10.5482 15.0781C10.4779 14.6719 10.6146 14.2617 10.9076 13.9727L13.3841 11.5547L9.96618 11.0586C9.55993 11 9.20837 10.7422 9.02477 10.375L7.49743 7.27344L5.97008 10.375V10.3711ZM6.65758 6.14844C7.00133 5.44922 7.99743 5.44922 8.34118 6.14844L10.1498 9.82031L14.1927 10.4102C14.9623 10.5234 15.267 11.4648 14.7123 12.0078L11.7865 14.8672L12.4779 18.9023C12.6107 19.668 11.806 20.2539 11.1185 19.8906L7.50133 17.9844L3.88024 19.8906C3.19274 20.2539 2.38805 19.668 2.52087 18.9023L3.21227 14.8672L0.282584 12.0078C-0.272103 11.4648 0.0364903 10.5195 0.802115 10.4102L4.84508 9.82031L6.65368 6.14844H6.65758ZM17.5013 6.25C17.8451 6.25 18.1263 6.53125 18.1263 6.875V8.125H19.3763C19.7201 8.125 20.0013 8.40625 20.0013 8.75C20.0013 9.09375 19.7201 9.375 19.3763 9.375H18.1263V10.625C18.1263 10.9688 17.8451 11.25 17.5013 11.25C17.1576 11.25 16.8763 10.9688 16.8763 10.625V9.375H15.6263C15.2826 9.375 15.0013 9.09375 15.0013 8.75C15.0013 8.40625 15.2826 8.125 15.6263 8.125H16.8763V6.875C16.8763 6.53125 17.1576 6.25 17.5013 6.25Z"
                      fill="black"
                    />
                  </svg>
                  {role === "employer"
                    ? t("schedule.leaveReviewWorker")
                    : t("schedule.leaveReview")}
                </div>
                <LinkArrowLeft />
              </div>
              <Divider />
            </Link>
          )}

          <div className="flex flex-row items-center w-full justify-between h-10 body-small font-semibold">
            <div className="flex flex-row items-center justify-center gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10 1.25C12.3206 1.25 14.5462 2.17187 16.1872 3.81282C17.8281 5.45376 18.75 7.67936 18.75 10C18.75 12.3206 17.8281 14.5462 16.1872 16.1872C14.5462 17.8281 12.3206 18.75 10 18.75C7.67936 18.75 5.45376 17.8281 3.81282 16.1872C2.17187 14.5462 1.25 12.3206 1.25 10C1.25 7.67936 2.17187 5.45376 3.81282 3.81282C5.45376 2.17187 7.67936 1.25 10 1.25ZM10 20C12.6522 20 15.1957 18.9464 17.0711 17.0711C18.9464 15.1957 20 12.6522 20 10C20 7.34784 18.9464 4.8043 17.0711 2.92893C15.1957 1.05357 12.6522 0 10 0C7.34784 0 4.8043 1.05357 2.92893 2.92893C1.05357 4.8043 0 7.34784 0 10C0 12.6522 1.05357 15.1957 2.92893 17.0711C4.8043 18.9464 7.34784 20 10 20ZM10 5C9.65625 5 9.375 5.28125 9.375 5.625V10.625C9.375 10.9688 9.65625 11.25 10 11.25C10.3438 11.25 10.625 10.9688 10.625 10.625V5.625C10.625 5.28125 10.3438 5 10 5ZM10.9375 13.75C10.9375 13.5014 10.8387 13.2629 10.6629 13.0871C10.4871 12.9113 10.2486 12.8125 10 12.8125C9.75136 12.8125 9.5129 12.9113 9.33709 13.0871C9.16127 13.2629 9.0625 13.5014 9.0625 13.75C9.0625 13.9986 9.16127 14.2371 9.33709 14.4129C9.5129 14.5887 9.75136 14.6875 10 14.6875C10.2486 14.6875 10.4871 14.5887 10.6629 14.4129C10.8387 14.2371 10.9375 13.9986 10.9375 13.75Z"
                  fill="black"
                />
              </svg>
              {t("schedule.reportProblem")}
            </div>
            <LinkArrowLeft />
          </div>
        </div>
      </aside>
    );
  }

  if (state.type === "day") {
    return (
      <aside className="min-w-[400px] relative h-full p-5 bg-white rounded-[15px] flex flex-col max-w-[400px] min-h-[1058] gap-8">
        <Button variant="outline" className="w-[120px]" onClick={onBack}>
          <span className="flex items-center gap-[2px] w-full">
            <LeftArrow /> {t("schedule.back")}
          </span>
        </Button>
        <h2 className="heading-2 mb-2">{state.date}</h2>
        <div className="space-y-3 max-h-[1000px] overflow-y-auto">
          {state.shifts.map((s: ShiftCard) => (
            <ShiftCardItem key={s.id} s={s} onClick={() => onSelectShift(s)} />
          ))}
        </div>
      </aside>
    );
  }

  if (state.type === "past") {
    return (
      <aside className="min-w-[400px] relative h-full p-5 bg-white rounded-[15px] flex flex-col max-w-[400px] min-h-[1058px] gap-8">
        <Button variant="outline" className="w-[120px]" onClick={onBack}>
          <span className="flex items-center gap-[2px] w-full">
            <LeftArrow /> {t("schedule.back")}
          </span>
        </Button>
        <h3 className="heading-3 mb-3">{t("schedule.pastShifts")}</h3>
        <div className="space-y-3 max-h-[1000px] overflow-y-auto">
          {state.items.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {t("schedule.noPastShifts")}
            </div>
          ) : (
            state.items.map((s: ShiftCard) => (
              <ShiftCardItem
                key={s.id}
                s={s}
                onClick={() => onSelectShift(s)}
                muted
              />
            ))
          )}
        </div>
      </aside>
    );
  }

  return (
    <aside className="min-w-[400px] relative h-full p-5 bg-white rounded-[15px] flex flex-col max-w-[400px] min-h-[1058px] max-h-[1058px] gap-8">
      <h2 className="heading-2">
        {role === "worker" ? t("schedule.allShifts", "All Shifts") : t("schedule.upcomingShifts")}
      </h2>
      <div className="flex flex-col min-h-0 flex-1 overflow-y-auto gap-3">
        {state.items.length === 0 && role === "worker" ? (
          <div className="text-sm text-muted-foreground">
            {t("schedule.noUpcomingShifts", "No upcoming shifts")}
          </div>
        ) : null}
        {state.items.map((s: ShiftCard) => (
          <ShiftCardItem
            key={s.id}
            s={s}
            // Offered / applied shifts aren't booked yet: open the shift modal (Accept/Decline
            // for offers) rather than the booked-shift detail panel.
            onClick={() =>
              s.pendingStatus && s.shiftId != null ? openShift(s.shiftId) : onSelectShift(s)
            }
            hideUser={role === "worker"}
          />
        ))}
      </div>
    </aside>
  );
}
